import React, { useMemo } from 'react'
import { Box, Divider, Stack, Skeleton, Alert, Typography, Button } from '@mui/material'
import { Link } from 'react-router'
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import { backgroundColors } from '../../../Common/Color/Color'
import { fontSizes, fontWeights } from '../../../Common/Typography/TypographyStyles'
import OverviewBadges from '../OverviewBadges'
import { formatDateToUTC, setDateFormat } from '../../../../utils/dateUtils'
import { Task } from '../../../../redux/task/types'
import { useLoading } from '../../../../context/LoadingContext'
import { useAppSelector } from '../../../../redux/store'
import useFetchAssignedTaskDetails from '../../../../hooks/taskHooks/useFetchAssignedTaskDetails'
import { selectUserTeamMemberInfo } from '../../../../redux/auth/selector'

const TaskSummaryBox = () => {
  const { loading } = useLoading()
  const teamMemberDetails = useAppSelector(selectUserTeamMemberInfo)

  const assignedFilters = useMemo(
    () => ({
      teamMemberEmail: teamMemberDetails.email,
      teamMemberWorkdayId: teamMemberDetails.workdayId,
    }),
    [teamMemberDetails.email, teamMemberDetails.workdayId],
  )

  const { tasks, error } = useFetchAssignedTaskDetails(assignedFilters)

  const filteredTaskData = () => {
    const today = new Date().getTime()
    return tasks
      .filter((task: Task) => !task.isArchived && task.status !== 'completed')
      .sort((a: Task, b: Task) => {
        const dateA = new Date(formatDateToUTC(a.dueDate !== null ? a.dueDate : '')).getTime()
        const dateB = new Date(formatDateToUTC(b.dueDate !== null ? b.dueDate : '')).getTime()
        const diffA = Math.abs(dateA - today)
        const diffB = Math.abs(dateB - today)
        return diffA - diffB
      })
      .slice(0, 5)
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          backgroundColor: backgroundColors.white,
          width: 'auto',
          borderRadius: 2,
          shadows: 1,
          p: 3,
        }}
      >
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
          py={1}
        >
          <Box
            sx={{
              fontSize: { xs: fontSizes.size14, md: fontSizes.size20 },
              fontWeight: fontWeights[500],
            }}
          >
            TASKS
          </Box>
          <Link to='/tasks' style={{ textDecoration: 'none' }}>
            <Button size='small' endIcon={<ArrowForwardIosRoundedIcon />}>
              View All
            </Button>
          </Link>
        </Box>
        <Box mt={2}>
          {loading ? (
            // Loading skeleton
            <Stack spacing={2}>
              {[...Array(5)].map((_, index) => (
                <Box key={index}>
                  <Skeleton variant='text' width='100%' />
                  <Skeleton variant='rectangular' height={30} />
                  <Skeleton variant='rectangular' height={20} width='60%' sx={{ marginTop: 1 }} />
                </Box>
              ))}
            </Stack>
          ) : error ? (
            // Error message
            <Alert severity='error'>{error}</Alert>
          ) : filteredTaskData().length > 0 ? (
            // Render the task data
            filteredTaskData().map((task: any) => (
              <Stack key={task.id}>
                <Divider />
                <Box display='flex' flexDirection='column' py={3} gap={4}>
                  <Stack
                    justifyContent={{ md: 'space-between' }}
                    direction={{ xs: 'column', md: 'row' }}
                    gap={{ xs: 1, md: 2 }}
                    alignItems={{ xs: 'flex-start', md: 'flex-start' }}
                  >
                    <Box
                      component='div'
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        maxWidth: '100%',
                      }}
                    >
                      <Box
                        component='div'
                        sx={{
                          fontSize: {
                            xs: fontSizes.size12,
                            md: fontSizes.size16,
                          },
                          fontWeight: fontWeights[700],
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto',
                          lineHeight: 1.3,
                        }}
                      >
                        {task.title}
                      </Box>
                    </Box>

                    <Box sx={{ flexShrink: 0 }}>
                      <OverviewBadges value={task.importance} badgeType='taskImportanceBadge' />
                    </Box>
                  </Stack>
                  <Box display='flex' alignItems='center' gap={1}>
                    <EventRoundedIcon
                      sx={{
                        fontSize: {
                          xs: fontSizes.size12,
                          md: fontSizes.size14,
                        },
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: {
                          xs: fontSizes.size12,
                          md: fontSizes.size14,
                        },
                      }}
                    >
                      {setDateFormat(task.dueDate)}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            ))
          ) : (
            <Alert severity='info'>No tasks available.</Alert>
          )}
        </Box>
      </Box>
    </React.Fragment>
  )
}

export default TaskSummaryBox
